/** JSON object received from the monitoring API. */
export type JsonRecord = Record<string, unknown>;

/** Complete dashboard payload assembled from read-only API endpoints. */
export interface DashboardData {
  readonly readiness: JsonRecord;
  readonly dataQuality: JsonRecord;
  readonly status: JsonRecord;
  readonly config: JsonRecord;
  readonly markets: readonly JsonRecord[];
  readonly positions: readonly JsonRecord[];
  readonly signals: readonly JsonRecord[];
  readonly attempts: readonly JsonRecord[];
  readonly activity: readonly JsonRecord[];
  readonly statistics: JsonRecord;
}

/** One level in a live executable order book. */
export interface LiveOrderBookLevel {
  readonly pricePercent1: number;
  readonly sizeShares: number;
}

/** Current level-two depth for one outcome token. */
export interface LiveOrderBook {
  readonly tokenId: string;
  readonly sourceTimestamp: string;
  readonly receivedAt: string;
  readonly bids: readonly LiveOrderBookLevel[];
  readonly asks: readonly LiveOrderBookLevel[];
}

/** Latest price state for one outcome token. */
export interface LiveTokenPrice {
  readonly tokenId: string;
  readonly lastTradePricePercent1: number | null;
  readonly lastTradeAt: string | null;
  readonly bestBidPercent1: number | null;
  readonly bestAskPercent1: number | null;
  readonly quoteAt: string | null;
}

/** Outcome-specific live state included in a market snapshot. */
export interface LiveMarketOutcome {
  readonly tokenId: string;
  readonly label: string;
  readonly price: LiveTokenPrice | null;
  readonly orderBook: LiveOrderBook | null;
}

/** Historical odds point reconstructed by the API. */
export interface MarketPriceHistoryPoint {
  readonly tokenId: string;
  readonly observedAt: string;
  readonly lastTradePricePercent1: number | null;
  readonly bestBidPercent1: number | null;
  readonly bestAskPercent1: number | null;
  readonly midpointPercent1: number | null;
}

/** Scoped live-market payload used by market and position modals. */
export interface MarketLiveData {
  readonly market: JsonRecord;
  readonly marketUrl: string;
  readonly outcomes: readonly LiveMarketOutcome[];
  readonly priceHistory: readonly MarketPriceHistoryPoint[];
}

/** One strategy row returned by the global account manager. */
export interface ManagedStrategy {
  readonly id: string;
  readonly key: string;
  readonly version: string;
  readonly name: string;
  readonly description: string;
  readonly family: string;
  readonly venue: string;
  readonly mode: 'PAPER' | 'LIVE';
  readonly enabled: boolean;
  readonly protected: boolean;
  readonly allocationMode: 'PERCENT' | 'DOLLARS';
  readonly allocationValue: number;
  readonly allocationPercent100: number;
  readonly allocatedCapitalDollars: number;
  readonly availableCapitalDollars: number;
  readonly lockedCapitalDollars: number;
  readonly realizedPnlDollars: number;
  readonly returnPercent100: number;
  readonly signals: number;
  readonly plans: number;
  readonly hypotheticalOrders: number;
  readonly fills: number;
  readonly openPositions: number;
  readonly uniqueMarkets: number;
  readonly uniqueMarketDays: number;
  readonly config: JsonRecord;
}

/** Global master-account and strategy registry response. */
export interface StrategyManagementData {
  readonly account: {
    readonly id: string;
    readonly initialEquityDollars: number;
    readonly equityDollars: number;
    readonly cashDollars: number;
    readonly lockedCapitalDollars: number;
  };
  readonly strategies: readonly ManagedStrategy[];
  readonly relationships: readonly ManagedRelationship[];
}

/** One reviewed relationship version displayed on the strategy page. */
export interface ManagedRelationship {
  readonly id: string;
  readonly relationshipKey: string;
  readonly version: string;
  readonly name: string;
  readonly relationshipType: 'EQUIVALENT_BINARY' | 'EXHAUSTIVE_BASKET' | 'RESULT_CONVERGENCE';
  readonly status: 'DRAFT' | 'VERIFIED' | 'EXPIRED' | 'REJECTED';
  readonly ruleReviewVersion: string;
  readonly effectiveFrom: string;
  readonly effectiveUntil: string | null;
  readonly verifiedBy: string | null;
  readonly verifiedAt: string | null;
}

/** One venue adapter status row. */
export interface VenueDataStatus {
  readonly venue: 'POLYMARKET' | 'KALSHI';
  readonly status: 'IDLE' | 'CONNECTING' | 'CONNECTED' | 'RECONNECTING' | 'BLOCKED';
  readonly credentialsReady: boolean;
  readonly subscribedInstrumentCount: number;
  readonly validBookCount: number;
  readonly gappedBookCount: number;
  readonly lastMessageAt: string | null;
  readonly lastError: string | null;
}

/** Global venue service and research runtime status. */
export interface VenueDataManagementStatus {
  readonly venues: readonly VenueDataStatus[];
  readonly retainedTradeCount: number;
  readonly runtime: {
    readonly enabledStrategyCount: number;
    readonly verifiedRelationshipCount: number;
    readonly evaluatorRunning: boolean;
  };
}

const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
/**
 * Fetches and validates a JSON response from the backend.
 * @param path - Absolute API path.
 * @returns Parsed JSON value.
 */
async function fetchJson(path: string): Promise<unknown> {
  const response = await fetch(`${apiBaseUrl}${path}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`${path} returned HTTP ${response.status}`);
  return response.json() as Promise<unknown>;
}

/**
 * Sends one JSON mutation and returns its typed JSON response.
 * @param path - Absolute API path.
 * @param method - HTTP mutation method.
 * @param body - Optional JSON body.
 * @returns Parsed response payload.
 */
async function mutateJson(path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown): Promise<unknown> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as JsonRecord;
    throw new Error(String(payload.message ?? payload.error ?? `${path} returned HTTP ${response.status}`));
  }
  return response.json() as Promise<unknown>;
}

/** @returns Current strategy registry, allocation, and performance state. */
export async function fetchStrategyManagement(): Promise<StrategyManagementData> {
  return await fetchJson('/api/strategy-management') as StrategyManagementData;
}

/** @returns Current global Polymarket, Kalshi, and evaluator status. */
export async function fetchVenueDataStatus(): Promise<VenueDataManagementStatus> {
  return await fetchJson('/api/venue-data/status') as VenueDataManagementStatus;
}

/** @returns Refreshed strategy state after a durable runtime reload. */
export async function reloadStrategies(): Promise<StrategyManagementData> {
  return await mutateJson('/api/strategy-management/reload', 'POST') as StrategyManagementData;
}

/** @returns Refreshed strategy state after an even-allocation reset. */
export async function resetEvenStrategyAllocations(): Promise<StrategyManagementData> {
  return await mutateJson('/api/strategy-management/allocations/reset-even', 'POST') as StrategyManagementData;
}

/**
 * Adjusts one sleeve and proportionally rebalances all other enabled sleeves.
 * @param strategyConfigId - Strategy version identifier.
 * @param mode - Percent or dollars.
 * @param value - Scale-1 percent or dollar amount.
 * @returns Refreshed strategy state.
 */
export async function adjustStrategyAllocation(
  strategyConfigId: string,
  mode: 'PERCENT' | 'DOLLARS',
  value: number,
): Promise<StrategyManagementData> {
  return await mutateJson(`/api/strategy-management/allocations/${encodeURIComponent(strategyConfigId)}`, 'PUT', { mode, value }) as StrategyManagementData;
}

/** @param strategyConfigId - Strategy version. @param enabled - Desired status. @returns Refreshed strategy state. */
export async function setStrategyEnabled(strategyConfigId: string, enabled: boolean): Promise<StrategyManagementData> {
  return await mutateJson(`/api/strategy-management/strategies/${encodeURIComponent(strategyConfigId)}/status`, 'PATCH', { enabled }) as StrategyManagementData;
}

/** @param strategyConfigId - Source version. @param config - Complete immutable new config. @returns Refreshed state. */
export async function createEditedStrategyVersion(strategyConfigId: string, config: JsonRecord): Promise<StrategyManagementData> {
  return await mutateJson(`/api/strategy-management/strategies/${encodeURIComponent(strategyConfigId)}/versions`, 'POST', config) as StrategyManagementData;
}

/** @param strategyConfigId - Disabled research version to archive. @returns Refreshed state. */
export async function deleteStrategyVersion(strategyConfigId: string): Promise<StrategyManagementData> {
  return await mutateJson(`/api/strategy-management/strategies/${encodeURIComponent(strategyConfigId)}`, 'DELETE') as StrategyManagementData;
}

/**
 * Fetches an operational status response even when its HTTP status intentionally indicates unready state.
 * @param path - Absolute health or readiness path.
 * @returns Parsed status plus the transport-level readiness result.
 */
async function fetchOperationalJson(path: string): Promise<JsonRecord> {
  const response = await fetch(`${apiBaseUrl}${path}`, { headers: { Accept: 'application/json' } });
  const payload = await response.json() as unknown;
  const record = typeof payload === 'object' && payload !== null ? payload as JsonRecord : {};
  return { ...record, httpOk: response.ok };
}

/**
 * Fetches all dashboard sections concurrently.
 * @returns Current dashboard data.
 */
export async function fetchDashboardData(): Promise<DashboardData> {
  const [readiness, dataQuality, status, config, markets, positions, signals, attempts, activity, statistics] = await Promise.all([
    fetchOperationalJson('/readyz'),
    fetchJson('/api/data-quality'),
    fetchJson('/api/status'),
    fetchJson('/api/config'),
    fetchJson('/api/markets'),
    fetchJson('/api/positions'),
    fetchJson('/api/signals'),
    fetchJson('/api/execution-attempts'),
    fetchJson('/api/activity'),
    fetchJson('/api/statistics'),
  ]);
  return {
    readiness: readiness as JsonRecord,
    dataQuality: dataQuality as JsonRecord,
    status: status as JsonRecord,
    config: config as JsonRecord,
    markets: markets as JsonRecord[],
    positions: positions as JsonRecord[],
    signals: signals as JsonRecord[],
    attempts: attempts as JsonRecord[],
    activity: activity as JsonRecord[],
    statistics: statistics as JsonRecord,
  };
}

/**
 * Fetches the bounded in-memory chart history and latest price/depth snapshot.
 * @param marketId - Stable local market identifier.
 * @returns Typed live-market payload.
 */
export async function fetchMarketLiveData(marketId: string): Promise<MarketLiveData> {
  return await fetchJson(`/api/markets/${encodeURIComponent(marketId)}/live`) as MarketLiveData;
}

/**
 * Derives the backend monitoring WebSocket URL for local and production builds.
 * @returns WebSocket URL.
 */
export function getWebSocketUrl(): string {
  const configured = import.meta.env.VITE_WS_URL as string | undefined;
  if (configured !== undefined && configured.length > 0) return configured;
  if (apiBaseUrl.length > 0) return `${apiBaseUrl.replace(/^http/, 'ws')}/ws`;
  return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
}

/**
 * Derives a scoped WebSocket URL that exists only while a market modal is open.
 * @param marketId - Stable local market identifier.
 * @returns Scoped live-market WebSocket URL.
 */
export function getMarketWebSocketUrl(marketId: string): string {
  const url = new URL(getWebSocketUrl());
  url.pathname = '/ws/market';
  url.search = '';
  url.searchParams.set('marketId', marketId);
  return url.toString();
}
